import { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/cn'

export interface ShiftTemplateCarouselProps {
  /** Rendered template cards, laid out in a horizontal, scrollable track. */
  children: React.ReactNode
  className?: string
}

/**
 * Horizontal carousel for shift templates. Templates slide in either direction
 * via the left/right arrows (one viewport-width page per click) and via native
 * horizontal scroll / trackpad / touch swipe. Arrows disable at each end.
 */
export function ShiftTemplateCarousel({
  children,
  className,
}: ShiftTemplateCarouselProps): React.ReactElement {
  const trackRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateArrows = useCallback(() => {
    const el = trackRef.current
    if (!el) return
    // 1px tolerance absorbs sub-pixel rounding at the extremes.
    setCanScrollLeft(el.scrollLeft > 1)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }, [])

  useEffect(() => {
    const el = trackRef.current
    if (!el) return
    updateArrows()
    el.addEventListener('scroll', updateArrows, { passive: true })
    // Re-evaluate when the container or its content resizes (cards load, viewport
    // changes) so the right arrow appears/disappears correctly.
    const observer = new ResizeObserver(updateArrows)
    observer.observe(el)
    return () => {
      el.removeEventListener('scroll', updateArrows)
      observer.disconnect()
    }
  }, [updateArrows, children])

  const scrollByPage = useCallback((direction: 1 | -1) => {
    const el = trackRef.current
    if (!el) return
    // Page by ~90% of the visible width so a sliver of the next card stays in
    // view as an affordance that there's more.
    const amount = Math.max(240, el.clientWidth * 0.9)
    el.scrollBy({ left: direction * amount, behavior: 'smooth' })
  }, [])

  useEffect(() => {
    const track = trackRef.current
    if (!track) return

    const handleWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return
      const canMove =
        event.deltaY > 0
          ? track.scrollLeft + track.clientWidth < track.scrollWidth - 1
          : track.scrollLeft > 1
      if (!canMove) return
      event.preventDefault()
      track.scrollLeft += event.deltaY
    }

    track.addEventListener('wheel', handleWheel, { passive: false })
    return () => track.removeEventListener('wheel', handleWheel)
  }, [])

  return (
    <div className={cn('relative min-w-0', className)}>
        <div className="absolute -top-10 right-0 flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={() => scrollByPage(-1)}
            disabled={!canScrollLeft}
            aria-label="Scroll templates left"
            title="Scroll templates left"
            className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-600 transition hover:bg-gray-50 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-35 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => scrollByPage(1)}
            disabled={!canScrollRight}
            aria-label="Scroll templates right"
            title="Scroll templates right"
            className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-600 transition hover:bg-gray-50 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-35 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            <ChevronRight className="h-4 w-4" aria-hidden />
          </button>
      </div>

      {/* Scrollable track */}
      <div
        ref={trackRef}
        role="region"
        aria-label="Shift templates"
        className="template-carousel-track flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth"
        onScroll={updateArrows}
      >
        {children}
      </div>
    </div>
  )
}

ShiftTemplateCarousel.displayName = 'ShiftTemplateCarousel'
